import { IconFa } from '@glow/ui-components'
import classNames from 'classnames'
import i18next from 'i18next'
import { useContext } from 'react'
import { ReactComponent as BringEnvironment } from '../../assets/icons/bring/bring-environment.svg'
import { ReactComponent as CalendarClock } from '../../assets/icons/calendar-clock.svg'
import { ReactComponent as IconDigitalIdentityDk } from '../../assets/icons/digital-identity/dk.svg'
import { ReactComponent as IconDigitalIdentityNo } from '../../assets/icons/digital-identity/no.svg'
import { ReactComponent as IconDigitalIdentitySe } from '../../assets/icons/digital-identity/se.svg'
import { ReactComponent as Door } from '../../assets/icons/door.svg'
import { ReactComponent as Environment } from '../../assets/icons/environment.svg'
import { ReactComponent as IkeaEvents } from '../../assets/icons/ikea/ikea-bell.svg'
import ikeaCalendar from '../../assets/icons/ikea/ikea-calendar.svg'
import ikeaCheck from '../../assets/icons/ikea/ikea-check.svg'
import ikeaDissatisfiedSmiley from '../../assets/icons/ikea/ikea-dissatisfied-smiley.svg'
import { ReactComponent as IkeaDoor } from '../../assets/icons/ikea/ikea-door.svg'
import ikeaEnvironment from '../../assets/icons/ikea/ikea-environment.svg'
import ikeaExclamation from '../../assets/icons/ikea/ikea-exclamation.svg'
import ikeaIdCard from '../../assets/icons/ikea/ikea-id-card.svg'
import { ReactComponent as IkeaAbout } from '../../assets/icons/ikea/ikea-package-info.svg'
import ikeaReceipt from '../../assets/icons/ikea/ikea-receipt.svg'
import ikeaThumbsUp from '../../assets/icons/ikea/ikea-thumbs-up.svg'
import { ReactComponent as IkeaTracking } from '../../assets/icons/ikea/ikea-where-is-package.svg'
import { ReactComponent as MovingParcel } from '../../assets/icons/moving-parcel.svg'
import { ReactComponent as PackageInfo } from '../../assets/icons/package-info.svg'
import { ReactComponent as ParcelLocker } from '../../assets/icons/parcel-locker.svg'
import { ReactComponent as SadComputer } from '../../assets/icons/sad-computer.svg'
import { ReactComponent as WhereIsPackage } from '../../assets/icons/where-is-package.svg'
import { CountryWithDigitalIdentity } from '../../trackdelivery/domain/DigitalIdentity'
import { ThemeContext } from '../contexts/ThemeContext'
import { isBringOrPostenTheme, isIkeaTheme } from '../utils/theme'

export function ExclamationIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img className="w-8 h-8" src={ikeaExclamation} alt="Exclamation mark in a circle" />
  ) : (
    <IconFa className="w-8 h-8" icon={['far', 'exclamation-triangle']} />
  )
}

export function EnvironmentIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img className="w-20 h-20" src={ikeaEnvironment} alt={'A charging cable'} />
  ) : isBringOrPostenTheme(theme) ? (
    <BringEnvironment className="w-12 h-12 text-primary-400" />
  ) : (
    <Environment className="w-12 h-12" />
  )
}

export function EventsIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? <IkeaEvents className="w-8 h-8" /> : <IconFa icon={['far', 'bell']} className="w-8 h-8" />
}

export function AboutIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? <IkeaAbout className="w-8 h-8" /> : <PackageInfo />
}

export function TrackingIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? <IkeaTracking className="w-8 h-8" /> : <WhereIsPackage />
}

export function ReceiptIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img className="w-8 h-8" src={ikeaReceipt} alt="A paper receipt" />
  ) : (
    <IconFa
      icon={['far', 'receipt']}
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
    />
  )
}

export function RecycleIcon() {
  const { theme } = useContext(ThemeContext)
  return (
    <IconFa
      icon={['far', 'recycle']}
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
    />
  )
}

export function SadIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img className="w-8 h-8" src={ikeaDissatisfiedSmiley} alt={'The letter i in a circle'} />
  ) : (
    <SadComputer className={isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y'} />
  )
}

export function CalendarIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img className="w-8 h-8" src={ikeaCalendar} alt={i18next.t('imageAlt.calendarClockIcon')} />
  ) : (
    <CalendarClock
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
    />
  )
}

export function ThumbsUpIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img className="w-8 h-8" src={ikeaThumbsUp} alt={i18next.t('imageAlt.thumbsUp')} />
  ) : (
    <IconFa
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
      icon={['far', 'thumbs-up']}
    />
  )
}

export function CheckIcon({ ariaHidden, className }: { className?: string; ariaHidden?: boolean }) {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img aria-hidden={ariaHidden} className="w-8 h-8" src={ikeaCheck} alt={i18next.t('imageAlt.checkIcon')} />
  ) : (
    <IconFa
      aria-hidden={ariaHidden}
      className={classNames(isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y', className)}
      icon={['fas', 'check-circle']}
    />
  )
}

export function ImportantIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img className="w-8 h-8" src={ikeaExclamation} alt={i18next.t('imageAlt.importantIcon')} />
  ) : (
    <IconFa className="w-8 h-8 text-danger-dark" icon={['fas', 'exclamation-triangle']} />
  )
}

export function HousingIcon() {
  const { theme } = useContext(ThemeContext)
  return (
    <IconFa
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
      icon={['far', 'house-building']}
    />
  )
}

export function ApartmentIcon(props: { extraStyle?: string }) {
  return <IconFa className={'w-8 h-8 ' + props.extraStyle} icon={['far', 'apartment']} />
}

export function HouseIcon() {
  return <IconFa className="w-8 h-8" icon={['far', 'house']} />
}

export function DoorIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <IkeaDoor className="w-8 h-8" />
  ) : (
    <Door
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
    />
  )
}

export function LocationIcon() {
  const { theme } = useContext(ThemeContext)
  return (
    <IconFa
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
      icon={['far', 'map-marker-alt']}
    />
  )
}

export function ParcelIcon() {
  const { theme } = useContext(ThemeContext)
  return <MovingParcel className={isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y'} />
}

export function ParcelLockerIcon() {
  const { theme } = useContext(ThemeContext)
  return <ParcelLocker className={isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y'} />
}

export function DollyIcon() {
  const { theme } = useContext(ThemeContext)
  return (
    <IconFa
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
      icon={['far', 'dolly']}
    />
  )
}

export function StopwatchIcon() {
  const { theme } = useContext(ThemeContext)
  return (
    <IconFa
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
      icon={['far', 'stopwatch']}
    />
  )
}

export function ErrorIcon() {
  return <IconFa className="w-8 h-8 text-danger-dark" icon={['far', 'question-circle']} />
}

export function IdCardIcon() {
  const { theme } = useContext(ThemeContext)
  return isIkeaTheme(theme) ? (
    <img className="w-8 h-8" src={ikeaIdCard} alt={i18next.t('imageAlt.idCard')} />
  ) : (
    <IconFa
      className={classNames('w-8 h-8', isBringOrPostenTheme(theme) ? 'text-primary-400-a11y' : 'text-primary-500-a11y')}
      icon={['far', 'credit-card']}
    />
  )
}

export function IdMethodIcon({ countryCode }: { countryCode: CountryWithDigitalIdentity }) {
  switch (countryCode) {
    case 'se':
      return <IconDigitalIdentitySe className="w-6 h-6" />
    case 'no':
      return <IconDigitalIdentityNo className="w-20 h-4" />
    case 'dk':
      return <IconDigitalIdentityDk className="w-20 h-4" />
  }
}
